import type { plugInToolbarActionParams } from '@wix/bi-logger-rce-data/v2/types';
import React from 'react';

import { useDevLogBi } from './../../ricos-context';
import type { EditorPluginName } from './../../ricos-schema/next-schema';

const BiContext = React.createContext<Bi>(null as unknown as Bi);

type Bi = {
  biLogger?: {
    logV2: (params: plugInToolbarActionParams) => void;
  };
};

export const BiContextProvider: React.FC<React.PropsWithChildren<Bi>> = ({
  children,
  biLogger,
}) => {
  return (
    <BiContext.Provider value={React.useMemo(() => ({ biLogger }), [biLogger])}>
      {children}
    </BiContext.Provider>
  );
};

export type LogBiFn = (params: { dataHook: string; value?: string | boolean | number }) => void;

export const useBiNext = (params?: {
  pluginId?: EditorPluginName;
  modalId?: string;
  nodeId?: string;
}): LogBiFn => {
  const devLogBi = useDevLogBi();

  if (devLogBi) {
    return params2 => devLogBi({ ...params, ...params2 });
  }

  return useDebouncedLogBi(params);
};

const useDebouncedLogBi = (
  params?: { pluginId?: string; modalId?: string; nodeId?: string },
  delay = 500
): LogBiFn => {
  const { biLogger } = React.useContext(BiContext) ?? {};
  const timeoutsRef = React.useRef(new Map());

  return React.useCallback<LogBiFn>(
    ({ dataHook, value }: { dataHook: string; value?: string | boolean | number }) => {
      if (!dataHook || !biLogger) {
        return;
      }

      if (timeoutsRef.current.has(dataHook)) {
        clearTimeout(timeoutsRef.current.get(dataHook));
      }

      const timeout = setTimeout(() => {
        biLogger.logV2?.({
          element_id: dataHook,
          value: value?.toString(),
          plugin_id: params?.pluginId,
          modal_id: params?.modalId,
          node_id: params?.nodeId,
        });

        timeoutsRef.current.delete(dataHook);
      }, delay);

      timeoutsRef.current.set(dataHook, timeout);
    },
    [biLogger, delay, params?.pluginId, params?.modalId]
  );
};
